import React from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

function fmtDuration(seconds) {
  if (!seconds || seconds < 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function fmtPct(fraction) {
  if (fraction == null) return '—';
  return `${Math.round(fraction * 100)}%`;
}

function fmtRatio(num, denom) {
  if (!denom) return '0 / 0';
  return `${num} / ${denom}`;
}

function FacePresenceCell({ pct, status }) {
  if (status !== 'complete' || pct == null) {
    return <span className="text-xs text-gray-400">{status || '—'}</span>;
  }
  const passed = pct >= 0.85;
  return (
    <span className={`text-xs font-medium tabular-nums ${passed ? 'text-emerald-700' : 'text-rose-700'}`}>
      {Math.round(pct * 100)}%
    </span>
  );
}

export default function SuperadminWebinarAttendance({ webinar, rows = [], resummarize_url: resummarizeUrl }) {
  const onResummarize = () => {
    if (!confirm('Recompute all attendance summaries for this webinar? Existing rows will be overwritten.')) return;
    router.post(resummarizeUrl, {}, { preserveScroll: true });
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
        <div>
          <Link href={`/superadmin/webinars/${webinar.id}`} className="text-sm text-indigo-600 hover:text-indigo-700">
            ← Back to webinar
          </Link>
          <div className="mt-2 flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Attendance details</h1>
              <p className="text-sm text-gray-600">{webinar.title}</p>
            </div>
            <button
              type="button"
              onClick={onResummarize}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer"
            >
              Recompute summaries
            </button>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-500">
            No registrations on this webinar yet.
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Viewer</th>
                  <th className="px-3 py-2 text-left font-semibold">Total time</th>
                  <th className="px-3 py-2 text-left font-semibold">Focused</th>
                  <th className="px-3 py-2 text-left font-semibold">Away</th>
                  <th className="px-3 py-2 text-left font-semibold">Tab switches</th>
                  <th className="px-3 py-2 text-left font-semibold">Polls</th>
                  <th className="px-3 py-2 text-left font-semibold">Chat</th>
                  <th className="px-3 py-2 text-left font-semibold">Slides</th>
                  <th className="px-3 py-2 text-left font-semibold">Face</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.registration_id} className="border-t border-gray-100">
                    <td className="px-3 py-2">
                      <div className="text-sm font-medium text-gray-900">{r.user_name}</div>
                      <div className="text-xs text-gray-500">{r.user_email}</div>
                    </td>
                    <td className="px-3 py-2 text-sm tabular-nums text-gray-700">
                      {r.has_summary ? fmtDuration(r.room_total_seconds) : <span className="text-xs text-gray-400">never joined</span>}
                    </td>
                    <td className="px-3 py-2 text-sm tabular-nums text-gray-700">
                      <div>{fmtDuration(r.focused_seconds)}</div>
                      <div className="text-xs text-gray-500">{fmtPct(r.focused_pct)}</div>
                    </td>
                    <td className="px-3 py-2 text-sm tabular-nums text-gray-700">
                      {fmtDuration((r.hidden_seconds || 0) + (r.blurred_seconds || 0))}
                    </td>
                    <td className="px-3 py-2 text-sm tabular-nums text-gray-700">{r.tab_hide_count}</td>
                    <td className="px-3 py-2 text-sm tabular-nums text-gray-700">
                      <div>{fmtRatio(r.polls_answered, r.polls_offered)}</div>
                      {r.poll_avg_response_seconds != null && (
                        <div className="text-xs text-gray-500">avg {r.poll_avg_response_seconds}s</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm tabular-nums text-gray-700">{r.chat_messages_sent}</td>
                    <td className="px-3 py-2 text-sm tabular-nums text-gray-700">{r.slides_viewed}</td>
                    <td className="px-3 py-2">
                      <FacePresenceCell pct={r.face_present_pct} status={r.face_analysis_status} />
                    </td>
                    <td className="px-3 py-2 text-right text-xs whitespace-nowrap">
                      <Link href={r.detail_url} className="text-indigo-600 hover:text-indigo-700">
                        Detail →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
